import React, { useState } from 'react';
import {
    Box,
    Button,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigationDrawer from './NavigationDrawer';
import FloatingNav from './FloatingNav';
import { brandColors } from '../Theme';

const HERO_IMAGE = '/mobile-hero-cal-ellie.jpg';
/** Calcifer gallery image 5 — indoor rug portrait (landscape, desktop hero). */
const DESKTOP_HERO_IMAGE = '/CalciferIndoor.jpg';

const heroPrimaryButtonSx = {
    py: 1.5,
    px: 3,
    borderRadius: 3,
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none' as const,
};

const heroSecondaryButtonSx = {
    ...heroPrimaryButtonSx,
    borderWidth: 2,
    bgcolor: '#fff',
    borderColor: brandColors.lavenderDark,
    color: brandColors.lavenderDark,
    '&:hover': {
        borderWidth: 2,
        bgcolor: '#fff',
        borderColor: brandColors.lavender,
        color: brandColors.lavender,
    },
};

const HeroEyebrow: React.FC = () => (
    <Typography
        component="p"
        sx={{
            fontSize: { xs: '0.8125rem', md: '0.875rem' },
            fontWeight: 500,
            color: 'rgba(26, 26, 26, 0.55)',
            mb: { xs: 2.5, md: 3 },
        }}
    >
        AKC &amp; UKC registered · Queer-owned · Austin, TX
    </Typography>
);

const HeroHeadline: React.FC = () => (
    <Typography
        component="p"
        sx={{
            fontSize: {
                xs: 'clamp(2rem, 8.5vw, 2.65rem)',
                md: 'clamp(2.5rem, 3.5vw, 3.75rem)',
            },
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.06,
            color: '#1a1a1a',
            maxWidth: { md: '14ch' },
        }}
    >
        Ethically bred{' '}
        <Box component="span" sx={{ color: brandColors.lavenderDark }}>
            Standard Poodles
        </Box>
        , rooted in Austin, TX
    </Typography>
);

const HeroSubtitle: React.FC = () => (
    <Typography
        sx={{
            mt: { xs: 2.25, md: 2.75 },
            fontSize: { xs: '1.05rem', md: '1.125rem' },
            lineHeight: 1.55,
            color: 'rgba(26, 26, 26, 0.68)',
            maxWidth: { md: '42ch' },
        }}
    >
        OFA health tested beyond CHIC standards. Thoughtful puppy placement, and dogs raised in
        our home—not a kennel.
    </Typography>
);

const HeroCTAs: React.FC = () => (
    <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mt: { xs: 3.5, md: 4 }, width: '100%' }}
    >
        <Button
            component={RouterLink}
            to="/upcoming-litters"
            variant="contained"
            color="primary"
            sx={{ ...heroPrimaryButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { md: 220 } }}
        >
            View upcoming litters
        </Button>
        <Button
            component={RouterLink}
            to="/our-poodles"
            variant="outlined"
            sx={{ ...heroSecondaryButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { md: 220 } }}
        >
            Meet our poodles
        </Button>
    </Stack>
);

const HomeHero: React.FC = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            <Box
                component="section"
                aria-label="Welcome"
                sx={{
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                    minHeight: { xs: 'auto', md: 'min(82vh, 820px)' },
                }}
            >
                {/* Mobile background */}
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${HERO_IMAGE})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 35%',
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(
                            180deg,
                            rgba(255, 255, 255, 0.92) 0%,
                            rgba(255, 255, 255, 0.78) 32%,
                            rgba(255, 255, 255, 0.45) 58%,
                            rgba(240, 238, 234, 1) 100%
                        )`,
                    }}
                />

                {/* Desktop — Calcifer indoor photo, flipped so dog faces into the layout */}
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        inset: 0,
                        bgcolor: '#f3f2f0',
                        backgroundImage: `url(${DESKTOP_HERO_IMAGE})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                        backgroundPosition: '40% center',
                        transform: 'scaleX(-1)',
                    }}
                />
                {/* Desktop — single overlay: soft left wash + full-width bottom blend (no hard edges) */}
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        inset: 0,
                        background: `
                            linear-gradient(
                                to bottom,
                                transparent 0%,
                                transparent 52%,
                                rgba(240, 238, 234, 0.18) 72%,
                                rgba(240, 238, 234, 0.62) 88%,
                                rgb(240, 238, 234) 100%
                            ),
                            linear-gradient(
                                to right,
                                rgba(255, 255, 255, 0.91) 0%,
                                rgba(255, 255, 255, 0.74) 20%,
                                rgba(255, 255, 255, 0.42) 36%,
                                rgba(255, 255, 255, 0.16) 48%,
                                rgba(255, 255, 255, 0.05) 58%,
                                rgba(255, 255, 255, 0) 72%
                            )
                        `,
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        maxWidth: 1400,
                        mx: 'auto',
                        px: { xs: 2, md: 4, lg: 5 },
                        pt: { xs: 'max(12px, env(safe-area-inset-top))', md: 3 },
                        pb: { xs: 2, md: 6 },
                    }}
                >
                    <FloatingNav menuOpen={menuOpen} onOpenMenu={() => setMenuOpen(true)} />

                    <Box
                        sx={{
                            pt: { xs: 4.5, md: 8 },
                            px: { xs: 0.5, md: 1 },
                            maxWidth: { md: '58%' },
                        }}
                    >
                        <HeroEyebrow />
                        <HeroHeadline />
                        <HeroSubtitle />
                        <HeroCTAs />
                    </Box>
                </Box>
            </Box>

            {isMobile && (
                <NavigationDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
            )}
        </>
    );
};

export default HomeHero;
